import * as pdfjsLib from 'pdfjs-dist';

// Use CDN for the worker to avoid Webpack configuration issues
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Target X coordinates for the 13 columns in the attendance PDF
const COLUMN_TARGETS = [18, 81, 117, 162, 189, 234, 288, 342, 378, 423, 468, 531, 639];

/**
 * Aligns row items to their correct column slots based on X coordinates.
 */
const alignPDFRow = (rowItems) => {
    const aligned = Array(COLUMN_TARGETS.length).fill('');
    
    rowItems.forEach(item => {
        const x = item.transform[4];
        const str = item.str.trim();
        
        let closestIdx = 0;
        let minDiff = Math.abs(x - COLUMN_TARGETS[0]);
        for (let i = 1; i < COLUMN_TARGETS.length; i++) {
            const diff = Math.abs(x - COLUMN_TARGETS[i]);
            if (diff < minDiff) {
                minDiff = diff;
                closestIdx = i;
            }
        }
        
        // Accept the alignment if it's within 30px of the target column X coordinate
        if (minDiff < 30) {
            if (aligned[closestIdx]) {
                aligned[closestIdx] += ' ' + str;
            } else {
                aligned[closestIdx] = str;
            }
        }
    });
    
    return aligned;
};

/**
 * Extracts text from a PDF file and attempts to format it as a 2D array 
 * (similar to an Excel sheet) so that the existing attendance parser can process it.
 * 
 * @param {File} file The PDF file
 * @returns {Promise<Array<Array<string>>>} A 2D array of strings representing rows and columns
 */
export const parsePDFTo2DArray = async (file) => {
    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        
        let allRows = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            
            // Group items by their Y coordinate to form rows
            // In PDF, Y coordinate is usually transform[5]. Bottom is 0.
            const rowsByY = {};
            
            textContent.items.forEach(item => {
                if (!item.str || item.str.trim() === '') return;
                
                // Round Y to nearest 5 to group items on the same visual line
                const y = Math.round(item.transform[5] / 5) * 5;
                
                if (!rowsByY[y]) {
                    rowsByY[y] = [];
                }
                rowsByY[y].push(item);
            });
            
            // Sort Y coordinates descending (top to bottom)
            const sortedY = Object.keys(rowsByY).map(Number).sort((a, b) => b - a);
            
            sortedY.forEach(y => {
                // Sort X coordinates ascending (left to right)
                const rowItems = rowsByY[y].sort((a, b) => a.transform[4] - b.transform[4]);
                
                const hasDate = rowItems.some(item => {
                    const val = item.str.trim();
                    return /^\d{1,2}[-/.][A-Za-z0-9]{3,}[-/.]\d{2,4}$/.test(val) || /^\d{1,2}[-/.][\d]{1,2}[-/.]\d{2,4}$/.test(val);
                });
                const isHeader = rowItems.some(item => item.str.trim().toLowerCase().includes('att. date'));
                
                if (isHeader || hasDate) {
                    allRows.push(alignPDFRow(rowItems));
                } else {
                    // Extract strings to form a row array normally (keeps employee details parsing intact)
                    const rowArray = rowItems.map(item => item.str.trim());
                    allRows.push(rowArray);
                }
            });
        }
        
        return allRows;
    } catch (error) {
        console.error('Error parsing PDF:', error);
        throw new Error('Failed to parse PDF file. Ensure it is a valid text-based PDF.');
    }
};
